// src/cockpit4/components/QualityManager.tsx
// ============================================
// P1-功能6：质量管理（项目中心）
//
// 功能：
//   1. 质量检查点和标准（每个项目可定义质量检查点清单）
//   2. 缺陷跟踪和统计（缺陷列表：严重程度/状态/发现日期/修复日期）
//   3. 质量趋势分析（按周/月统计缺陷数，recharts line chart）
//   4. 质量改进建议（基于缺陷高发领域生成建议）
//   5. 质量报告自动生成（写入 Vault：10_Operations（运营层）/Reports/质量报告-{date}.md）
//
// 数据来源：
//   - localStorage 持久化缺陷记录（xeaos_quality_issues）
//   - localStorage 持久化质量检查点（xeaos_quality_checkpoints）
//   - localStorage 读取关联项目（xeaos_projects_custom）
// ============================================

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldCheck,
  Plus,
  X,
  RefreshCw,
  Loader2,
  Trash2,
  Pencil,
  Bug,
  CheckCircle2,
  AlertCircle,
  AlertOctagon,
  TrendingUp,
  Lightbulb,
  FileText,
  Download,
  Calendar,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";

// ============================================
// 类型定义
// ============================================

/** 缺陷严重程度 */
export type DefectSeverity = "critical" | "major" | "minor";

/** 缺陷状态 */
export type DefectStatus = "open" | "fixing" | "fixed";

/** 缺陷记录 */
export interface QualityIssue {
  id: string;
  title: string;
  description: string;
  severity: DefectSeverity;
  status: DefectStatus;
  /** 缺陷类别（如：功能/性能/安全/UI/兼容性） */
  category: string;
  /** 关联项目 ID */
  projectId?: string;
  /** 发现日期 YYYY-MM-DD */
  foundDate: string;
  /** 修复日期 YYYY-MM-DD */
  fixedDate?: string;
  createdAt: string;
  updatedAt: string;
}

/** 质量检查点 */
export interface QualityCheckpoint {
  id: string;
  projectId?: string;
  name: string;
  standard: string;
  status: "pending" | "pass" | "fail";
  createdAt: string;
  updatedAt: string;
}

// ============================================
// 常量配置
// ============================================

const ISSUES_KEY = "xeaos_quality_issues";
const CHECKPOINTS_KEY = "xeaos_quality_checkpoints";
const PROJECTS_KEY = "xeaos_projects_custom";

const SEVERITY_META: Record<
  DefectSeverity,
  { label: string; color: string; icon: typeof AlertCircle }
> = {
  critical: { label: "致命", color: "#EF4444", icon: AlertOctagon },
  major: { label: "严重", color: "#F59E0B", icon: AlertCircle },
  minor: { label: "轻微", color: "#3B82F6", icon: Bug },
};

const STATUS_META: Record<
  DefectStatus,
  { label: string; color: string; icon: typeof AlertCircle }
> = {
  open: { label: "待处理", color: "#EF4444", icon: AlertCircle },
  fixing: { label: "修复中", color: "#F59E0B", icon: Bug },
  fixed: { label: "已修复", color: "#10B981", icon: CheckCircle2 },
};

const CHECKPOINT_STATUS_META: Record<
  QualityCheckpoint["status"],
  { label: string; color: string }
> = {
  pending: { label: "待检查", color: "#9CA3AF" },
  pass: { label: "通过", color: "#10B981" },
  fail: { label: "不通过", color: "#EF4444" },
};

const VAULT_REPORT_DIR = "10_Operations（运营层）/Reports";

// ============================================
// localStorage 持久化
// ============================================

function loadIssues(): QualityIssue[] {
  try {
    const raw = localStorage.getItem(ISSUES_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw) as QualityIssue[];
    if (!Array.isArray(list)) return [];
    return list;
  } catch {
    return [];
  }
}

function saveIssues(issues: QualityIssue[]): void {
  try {
    localStorage.setItem(ISSUES_KEY, JSON.stringify(issues));
  } catch {
    // 静默降级
  }
}

function loadCheckpoints(): QualityCheckpoint[] {
  try {
    const raw = localStorage.getItem(CHECKPOINTS_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw) as QualityCheckpoint[];
    if (!Array.isArray(list)) return [];
    return list;
  } catch {
    return [];
  }
}

function saveCheckpoints(checkpoints: QualityCheckpoint[]): void {
  try {
    localStorage.setItem(CHECKPOINTS_KEY, JSON.stringify(checkpoints));
  } catch {
    // 静默降级
  }
}

function loadProjectOptions(): { id: string; name: string }[] {
  try {
    const raw = localStorage.getItem(PROJECTS_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw) as Array<{ id: string; name: string }>;
    if (!Array.isArray(list)) return [];
    return list.map((p) => ({ id: p.id, name: p.name }));
  } catch {
    return [];
  }
}

// ============================================
// 工具函数
// ============================================

function formatDate(iso: string | undefined): string {
  if (!iso) return "—";
  return iso.slice(0, 10);
}

/** 获取某日期所在的周标识（YYYY-Www） */
function getWeekKey(dateStr: string): string {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "unknown";
  const thursday = new Date(d);
  thursday.setDate(d.getDate() + (4 - (d.getDay() || 7)));
  const yearStart = new Date(thursday.getFullYear(), 0, 1);
  const weekNo = Math.ceil(
    ((thursday.getTime() - yearStart.getTime()) / 86400000 + 1) / 7
  );
  return `${thursday.getFullYear()}-W${String(weekNo).padStart(2, "0")}`;
}

/** 获取某日期所在的月标识（YYYY-MM） */
function getMonthKey(dateStr: string): string {
  if (!dateStr) return "unknown";
  return dateStr.slice(0, 7);
}

// ============================================
// 主组件
// ============================================

export default function QualityManager() {
  const [issues, setIssues] = useState<QualityIssue[]>([]);
  const [checkpoints, setCheckpoints] = useState<QualityCheckpoint[]>([]);
  const [projectOptions, setProjectOptions] = useState<
    { id: string; name: string }[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<"issues" | "checkpoints" | "trend">(
    "issues"
  );
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [editingIssue, setEditingIssue] = useState<QualityIssue | null>(null);
  const [showCheckpointModal, setShowCheckpointModal] = useState(false);
  const [editingCheckpoint, setEditingCheckpoint] =
    useState<QualityCheckpoint | null>(null);
  const [trendMode, setTrendMode] = useState<"week" | "month">("week");
  const [reportStatus, setReportStatus] = useState<{
    loading: boolean;
    message: string | null;
  }>({ loading: false, message: null });

  // ============================================
  // 加载数据
  // ============================================
  const loadData = useCallback(() => {
    setLoading(true);
    setIssues(loadIssues());
    setCheckpoints(loadCheckpoints());
    setProjectOptions(loadProjectOptions());
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ============================================
  // 缺陷 CRUD
  // ============================================
  const handleSaveIssue = useCallback(
    (data: Omit<QualityIssue, "id" | "createdAt" | "updatedAt">) => {
      const now = new Date().toISOString();
      if (editingIssue) {
        const updated = issues.map((i) =>
          i.id === editingIssue.id
            ? {
                ...i,
                ...data,
                // 状态变为 fixed 时自动填入修复日期
                fixedDate:
                  data.status === "fixed" && !i.fixedDate
                    ? formatDate(now)
                    : data.fixedDate,
                updatedAt: now,
              }
            : i
        );
        setIssues(updated);
        saveIssues(updated);
      } else {
        const newIssue: QualityIssue = {
          ...data,
          id: `issue_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          createdAt: now,
          updatedAt: now,
        };
        const updated = [newIssue, ...issues];
        setIssues(updated);
        saveIssues(updated);
      }
      setShowIssueModal(false);
      setEditingIssue(null);
    },
    [editingIssue, issues]
  );

  const handleDeleteIssue = useCallback(
    (issueId: string) => {
      const updated = issues.filter((i) => i.id !== issueId);
      setIssues(updated);
      saveIssues(updated);
    },
    [issues]
  );

  const handleIssueStatusChange = useCallback(
    (issueId: string, status: DefectStatus) => {
      const now = new Date().toISOString();
      const today = formatDate(now);
      const updated = issues.map((i) =>
        i.id === issueId
          ? {
              ...i,
              status,
              fixedDate:
                status === "fixed" ? i.fixedDate || today : i.fixedDate,
              updatedAt: now,
            }
          : i
      );
      setIssues(updated);
      saveIssues(updated);
    },
    [issues]
  );

  // ============================================
  // 检查点 CRUD
  // ============================================
  const handleSaveCheckpoint = useCallback(
    (data: Omit<QualityCheckpoint, "id" | "createdAt" | "updatedAt">) => {
      const now = new Date().toISOString();
      if (editingCheckpoint) {
        const updated = checkpoints.map((c) =>
          c.id === editingCheckpoint.id
            ? { ...c, ...data, updatedAt: now }
            : c
        );
        setCheckpoints(updated);
        saveCheckpoints(updated);
      } else {
        const newCp: QualityCheckpoint = {
          ...data,
          id: `cp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          createdAt: now,
          updatedAt: now,
        };
        const updated = [newCp, ...checkpoints];
        setCheckpoints(updated);
        saveCheckpoints(updated);
      }
      setShowCheckpointModal(false);
      setEditingCheckpoint(null);
    },
    [editingCheckpoint, checkpoints]
  );

  const handleDeleteCheckpoint = useCallback(
    (cpId: string) => {
      const updated = checkpoints.filter((c) => c.id !== cpId);
      setCheckpoints(updated);
      saveCheckpoints(updated);
    },
    [checkpoints]
  );

  const handleCheckpointStatusChange = useCallback(
    (cpId: string, status: QualityCheckpoint["status"]) => {
      const now = new Date().toISOString();
      const updated = checkpoints.map((c) =>
        c.id === cpId ? { ...c, status, updatedAt: now } : c
      );
      setCheckpoints(updated);
      saveCheckpoints(updated);
    },
    [checkpoints]
  );

  // ============================================
  // 统计与趋势
  // ============================================
  const stats = useMemo(() => {
    return {
      total: issues.length,
      open: issues.filter((i) => i.status === "open").length,
      fixing: issues.filter((i) => i.status === "fixing").length,
      fixed: issues.filter((i) => i.status === "fixed").length,
      critical: issues.filter((i) => i.severity === "critical").length,
    };
  }, [issues]);

  /** 趋势数据：按周/月统计发现与修复数 */
  const trendData = useMemo(() => {
    const foundMap = new Map<string, number>();
    const fixedMap = new Map<string, number>();
    const keyFn = trendMode === "week" ? getWeekKey : getMonthKey;

    issues.forEach((i) => {
      const fk = keyFn(i.foundDate);
      foundMap.set(fk, (foundMap.get(fk) ?? 0) + 1);
      if (i.fixedDate) {
        const xk = keyFn(i.fixedDate);
        fixedMap.set(xk, (fixedMap.get(xk) ?? 0) + 1);
      }
    });

    const keys = Array.from(
      new Set([...foundMap.keys(), ...fixedMap.keys()])
    ).sort();
    return keys.map((k) => ({
      period: k,
      发现: foundMap.get(k) ?? 0,
      修复: fixedMap.get(k) ?? 0,
    }));
  }, [issues, trendMode]);

  /** 质量改进建议：基于缺陷高发类别 */
  const improvementSuggestions = useMemo(() => {
    const categoryCount = new Map<string, number>();
    issues.forEach((i) => {
      if (i.status !== "fixed") {
        categoryCount.set(i.category, (categoryCount.get(i.category) ?? 0) + 1);
      }
    });
    const sorted = Array.from(categoryCount.entries()).sort(
      (a, b) => b[1] - a[1]
    );
    return sorted.slice(0, 3).map(([cat, count]) => {
      let advice = "";
      if (cat === "功能") advice = "建议加强需求评审和单元测试覆盖。";
      else if (cat === "性能") advice = "建议引入性能基准测试和压测流程。";
      else if (cat === "安全") advice = "建议开展安全审计和代码扫描。";
      else if (cat === "UI") advice = "建议建立设计规范和视觉走查机制。";
      else if (cat === "兼容性")
        advice = "建议建立多环境测试矩阵和自动化兼容性测试。";
      else advice = "建议针对该领域加强检查点和回归测试。";
      return { category: cat, count, advice };
    });
  }, [issues]);

  // 项目名映射
  const projectNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    projectOptions.forEach((p) => {
      map[p.id] = p.name;
    });
    return map;
  }, [projectOptions]);

  // ============================================
  // 生成质量报告并写入 Vault
  // ============================================
  const generateReport = useCallback(async () => {
    setReportStatus({ loading: true, message: null });
    try {
      const today = formatDate(new Date().toISOString());
      const md = buildQualityReportMarkdown({
        date: today,
        issues,
        checkpoints,
        stats,
        suggestions: improvementSuggestions,
        projectNameMap,
      });
      const path = `${VAULT_REPORT_DIR}/质量报告-${today}.md`;
      const resp = await fetch("/vault-api/write", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ path, content: md }),
      });
      if (resp.ok) {
        const json = await resp.json();
        if (json?.success !== false) {
          setReportStatus({
            loading: false,
            message: `✓ 报告已生成：${path}`,
          });
          return;
        }
      }
      setReportStatus({
        loading: false,
        message: `⚠ Vault 写入返回非成功状态，请检查 vault-api 服务。路径：${path}`,
      });
    } catch (err: any) {
      setReportStatus({
        loading: false,
        message: `✕ 生成失败：${err?.message || String(err)}`,
      });
    }
  }, [issues, checkpoints, stats, improvementSuggestions, projectNameMap]);

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-teal-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-teal-500/15 border border-teal-500/30">
            <ShieldCheck size={13} className="text-teal-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">
              质量管理
            </h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Quality Manager · 共 {stats.total} 缺陷 · 待处理 {stats.open} ·
              修复中 {stats.fixing} · 致命 {stats.critical}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* 生成报告 */}
          <button
            onClick={generateReport}
            disabled={reportStatus.loading}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-teal-500/15 border border-teal-500/30 hover:bg-teal-500/25 transition-colors disabled:opacity-50"
            title="生成 Markdown 质量报告并写入 Vault"
          >
            {reportStatus.loading ? (
              <Loader2 size={11} className="text-teal-400 animate-spin" />
            ) : (
              <FileText size={11} className="text-teal-400" />
            )}
            <span className="text-[9px] font-mono text-teal-300 uppercase tracking-wider">
              生成报告
            </span>
          </button>

          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.08] transition-colors disabled:opacity-50"
          >
            {loading ? (
              <Loader2 size={11} className="text-gray-400 animate-spin" />
            ) : (
              <RefreshCw size={11} className="text-gray-400" />
            )}
          </button>
        </div>
      </div>

      {/* 报告生成状态 */}
      {reportStatus.message && (
        <div className="px-4 py-1.5 border-b border-white/[0.04] bg-teal-500/[0.04]">
          <span className="text-[9px] font-mono text-teal-300">
            {reportStatus.message}
          </span>
        </div>
      )}

      {/* KPI 行 */}
      <div className="grid grid-cols-4 gap-2 px-4 py-2 border-b border-white/[0.04]">
        <KpiCell label="缺陷总数" value={String(stats.total)} color="#3B82F6" />
        <KpiCell
          label="待处理"
          value={String(stats.open)}
          color="#EF4444"
        />
        <KpiCell
          label="已修复"
          value={String(stats.fixed)}
          color="#10B981"
        />
        <KpiCell
          label="修复率"
          value={
            stats.total > 0
              ? `${Math.round((stats.fixed / stats.total) * 100)}%`
              : "—"
          }
          color="#34D399"
        />
      </div>

      {/* 视图切换 + 新增按钮 */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-white/[0.04]">
        <div className="flex items-center rounded-md border border-white/[0.06] overflow-hidden">
          <button
            onClick={() => setView("issues")}
            className={`px-2 py-1 text-[9px] font-mono uppercase tracking-wider transition-colors ${
              view === "issues"
                ? "bg-teal-500/20 text-teal-300"
                : "bg-white/[0.02] text-gray-500 hover:bg-white/[0.04]"
            }`}
          >
            缺陷列表
          </button>
          <button
            onClick={() => setView("checkpoints")}
            className={`px-2 py-1 text-[9px] font-mono uppercase tracking-wider transition-colors ${
              view === "checkpoints"
                ? "bg-teal-500/20 text-teal-300"
                : "bg-white/[0.02] text-gray-500 hover:bg-white/[0.04]"
            }`}
          >
            检查点
          </button>
          <button
            onClick={() => setView("trend")}
            className={`px-2 py-1 text-[9px] font-mono uppercase tracking-wider transition-colors ${
              view === "trend"
                ? "bg-teal-500/20 text-teal-300"
                : "bg-white/[0.02] text-gray-500 hover:bg-white/[0.04]"
            }`}
          >
            趋势分析
          </button>
        </div>

        {view === "issues" && (
          <button
            onClick={() => {
              setEditingIssue(null);
              setShowIssueModal(true);
            }}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-teal-500/15 border border-teal-500/30 hover:bg-teal-500/25 transition-colors"
          >
            <Plus size={11} className="text-teal-400" />
            <span className="text-[9px] font-mono text-teal-300 uppercase tracking-wider">
              新增缺陷
            </span>
          </button>
        )}
        {view === "checkpoints" && (
          <button
            onClick={() => {
              setEditingCheckpoint(null);
              setShowCheckpointModal(true);
            }}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-teal-500/15 border border-teal-500/30 hover:bg-teal-500/25 transition-colors"
          >
            <Plus size={11} className="text-teal-400" />
            <span className="text-[9px] font-mono text-teal-300 uppercase tracking-wider">
              新增检查点
            </span>
          </button>
        )}
        {view === "trend" && (
          <div className="flex items-center rounded-md border border-white/[0.06] overflow-hidden">
            <button
              onClick={() => setTrendMode("week")}
              className={`px-2 py-1 text-[9px] font-mono uppercase tracking-wider transition-colors ${
                trendMode === "week"
                  ? "bg-teal-500/20 text-teal-300"
                  : "bg-white/[0.02] text-gray-500 hover:bg-white/[0.04]"
              }`}
            >
              按周
            </button>
            <button
              onClick={() => setTrendMode("month")}
              className={`px-2 py-1 text-[9px] font-mono uppercase tracking-wider transition-colors ${
                trendMode === "month"
                  ? "bg-teal-500/20 text-teal-300"
                  : "bg-white/[0.02] text-gray-500 hover:bg-white/[0.04]"
              }`}
            >
              按月
            </button>
          </div>
        )}
      </div>

      {/* 主体内容 */}
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 size={20} className="animate-spin text-teal-400" />
            <span className="ml-2 text-[11px] font-mono text-gray-500">
              加载质量数据...
            </span>
          </div>
        ) : view === "issues" ? (
          /* 缺陷列表 */
          issues.length === 0 ? (
            <EmptyState
              icon={Bug}
              text="暂无缺陷记录"
              hint="点击「新增缺陷」记录第一个质量问题"
            />
          ) : (
            <div className="space-y-1.5 max-h-[460px] overflow-y-auto">
              {issues.map((i) => (
                <IssueRow
                  key={i.id}
                  issue={i}
                  projectName={i.projectId ? projectNameMap[i.projectId] : undefined}
                  onEdit={() => {
                    setEditingIssue(i);
                    setShowIssueModal(true);
                  }}
                  onDelete={() => handleDeleteIssue(i.id)}
                  onStatusChange={(s) => handleIssueStatusChange(i.id, s)}
                />
              ))}
            </div>
          )
        ) : view === "checkpoints" ? (
          /* 检查点列表 */
          checkpoints.length === 0 ? (
            <EmptyState
              icon={ShieldCheck}
              text="暂无质量检查点"
              hint="点击「新增检查点」定义质量标准"
            />
          ) : (
            <div className="space-y-1.5 max-h-[460px] overflow-y-auto">
              {checkpoints.map((c) => (
                <CheckpointRow
                  key={c.id}
                  checkpoint={c}
                  projectName={
                    c.projectId ? projectNameMap[c.projectId] : undefined
                  }
                  onEdit={() => {
                    setEditingCheckpoint(c);
                    setShowCheckpointModal(true);
                  }}
                  onDelete={() => handleDeleteCheckpoint(c.id)}
                  onStatusChange={(s) => handleCheckpointStatusChange(c.id, s)}
                />
              ))}
            </div>
          )
        ) : (
          /* 趋势分析 + 改进建议 */
          <div className="space-y-3">
            <div className="flex items-center gap-1.5">
              <TrendingUp size={11} className="text-teal-400" />
              <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                质量趋势（发现 vs 修复）
              </span>
            </div>
            {trendData.length === 0 ? (
              <div className="text-center text-[10px] font-mono text-gray-600 py-8">
                暂无趋势数据
              </div>
            ) : (
              <div className="h-[240px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData}>
                    <CartesianGrid stroke="#ffffff10" strokeDasharray="3 3" />
                    <XAxis
                      dataKey="period"
                      tick={{
                        fill: "#9CA3AF",
                        fontSize: 9,
                        fontFamily: "monospace",
                      }}
                      axisLine={{ stroke: "#ffffff20" }}
                    />
                    <YAxis
                      tick={{
                        fill: "#9CA3AF",
                        fontSize: 10,
                        fontFamily: "monospace",
                      }}
                      axisLine={{ stroke: "#ffffff20" }}
                      allowDecimals={false}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "#0F1623",
                        border: "1px solid #ffffff20",
                        borderRadius: 6,
                        fontSize: 11,
                        fontFamily: "monospace",
                      }}
                      labelStyle={{ color: "#E5E7EB" }}
                    />
                    <Legend
                      wrapperStyle={{
                        fontSize: 10,
                        fontFamily: "monospace",
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="发现"
                      stroke="#EF4444"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="修复"
                      stroke="#10B981"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* 改进建议 */}
            <div className="mt-2">
              <div className="flex items-center gap-1.5 mb-1.5">
                <Lightbulb size={11} className="text-purple-400" />
                <span className="text-[10px] font-mono text-purple-400 uppercase tracking-wider">
                  质量改进建议
                </span>
              </div>
              {improvementSuggestions.length === 0 ? (
                <div className="text-[10px] font-mono text-gray-600 px-2 py-2">
                  暂无未修复缺陷，无需改进建议
                </div>
              ) : (
                <div className="space-y-1">
                  {improvementSuggestions.map((s) => (
                    <div
                      key={s.category}
                      className="flex items-start gap-2 px-2 py-1.5 rounded bg-purple-500/[0.04] border border-purple-500/20"
                    >
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-mono bg-purple-500/15 text-purple-300 flex-shrink-0">
                        {s.category}（{s.count}）
                      </span>
                      <span className="text-[10px] text-gray-300 leading-snug">
                        {s.advice}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* 缺陷编辑弹窗 */}
      <AnimatePresence>
        {showIssueModal && (
          <IssueEditModal
            issue={editingIssue}
            projectOptions={projectOptions}
            onSubmit={handleSaveIssue}
            onClose={() => {
              setShowIssueModal(false);
              setEditingIssue(null);
            }}
          />
        )}
      </AnimatePresence>

      {/* 检查点编辑弹窗 */}
      <AnimatePresence>
        {showCheckpointModal && (
          <CheckpointEditModal
            checkpoint={editingCheckpoint}
            projectOptions={projectOptions}
            onSubmit={handleSaveCheckpoint}
            onClose={() => {
              setShowCheckpointModal(false);
              setEditingCheckpoint(null);
            }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============================================
// 生成质量报告 Markdown
// ============================================
function buildQualityReportMarkdown(args: {
  date: string;
  issues: QualityIssue[];
  checkpoints: QualityCheckpoint[];
  stats: {
    total: number;
    open: number;
    fixing: number;
    fixed: number;
    critical: number;
  };
  suggestions: Array<{ category: string; count: number; advice: string }>;
  projectNameMap: Record<string, string>;
}): string {
  const { date, issues, checkpoints, stats, suggestions, projectNameMap } =
    args;
  const fixRate =
    stats.total > 0 ? Math.round((stats.fixed / stats.total) * 100) : 0;

  const lines: string[] = [];
  lines.push(`# 质量报告 · ${date}`);
  lines.push("");
  lines.push(`> 自动生成于 ${new Date().toISOString()} · 由 QualityManager 生成`);
  lines.push("");
  lines.push("## 一、质量概览");
  lines.push("");
  lines.push(`- 缺陷总数：**${stats.total}**`);
  lines.push(`- 待处理：**${stats.open}**`);
  lines.push(`- 修复中：**${stats.fixing}**`);
  lines.push(`- 已修复：**${stats.fixed}**（修复率 ${fixRate}%）`);
  lines.push(`- 致命缺陷：**${stats.critical}**`);
  lines.push("");

  lines.push("## 二、缺陷明细");
  lines.push("");
  if (issues.length === 0) {
    lines.push("暂无缺陷记录。");
  } else {
    lines.push("| 标题 | 严重程度 | 状态 | 类别 | 项目 | 发现日期 | 修复日期 |");
    lines.push("| --- | --- | --- | --- | --- | --- | --- |");
    issues.forEach((i) => {
      lines.push(
        `| ${i.title} | ${SEVERITY_META[i.severity].label} | ${STATUS_META[i.status].label} | ${i.category} | ${
          i.projectId ? projectNameMap[i.projectId] ?? "—" : "—"
        } | ${i.foundDate} | ${i.fixedDate ?? "—"} |`
      );
    });
  }
  lines.push("");

  lines.push("## 三、质量检查点");
  lines.push("");
  if (checkpoints.length === 0) {
    lines.push("暂无质量检查点。");
  } else {
    lines.push("| 检查点 | 标准 | 状态 | 项目 |");
    lines.push("| --- | --- | --- | --- |");
    checkpoints.forEach((c) => {
      lines.push(
        `| ${c.name} | ${c.standard} | ${CHECKPOINT_STATUS_META[c.status].label} | ${
          c.projectId ? projectNameMap[c.projectId] ?? "—" : "—"
        } |`
      );
    });
  }
  lines.push("");

  lines.push("## 四、改进建议");
  lines.push("");
  if (suggestions.length === 0) {
    lines.push("暂无未修复缺陷，当前质量状况良好。");
  } else {
    suggestions.forEach((s) => {
      lines.push(`- **${s.category}**（${s.count} 个未修复）：${s.advice}`);
    });
  }
  lines.push("");
  lines.push("---");
  lines.push("");
  lines.push("*本报告由 XEAOS 项目中心 · 质量管理自动生成*");
  return lines.join("\n");
}

// ============================================
// KPI 单元格
// ============================================
function KpiCell({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color: string;
}) {
  return (
    <div className="rounded-lg border border-white/[0.04] bg-white/[0.01] px-3 py-1.5">
      <div className="text-[8px] font-mono text-gray-500 uppercase tracking-wider">
        {label}
      </div>
      <div
        className="text-[16px] font-mono font-semibold mt-0.5"
        style={{ color }}
      >
        {value}
      </div>
    </div>
  );
}

// ============================================
// 空状态
// ============================================
function EmptyState({
  icon: Icon,
  text,
  hint,
}: {
  icon: typeof Bug;
  text: string;
  hint: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-center">
      <Icon size={28} className="text-gray-700 mb-2" />
      <p className="text-[11px] text-gray-500 mb-0.5">{text}</p>
      <p className="text-[10px] font-mono text-gray-600">{hint}</p>
    </div>
  );
}

// ============================================
// 缺陷行
// ============================================
function IssueRow({
  issue,
  projectName,
  onEdit,
  onDelete,
  onStatusChange,
}: {
  issue: QualityIssue;
  projectName?: string;
  onEdit: () => void;
  onDelete: () => void;
  onStatusChange: (status: DefectStatus) => void;
}) {
  const sevMeta = SEVERITY_META[issue.severity];
  const statMeta = STATUS_META[issue.status];
  const SIcon = sevMeta.icon;

  return (
    <div
      className="rounded-md border bg-white/[0.02] p-2.5"
      style={{ borderColor: `${sevMeta.color}30` }}
    >
      <div className="flex items-center gap-2">
        {/* 严重度图标 */}
        <div
          className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0"
          style={{
            background: `${sevMeta.color}15`,
            border: `1px solid ${sevMeta.color}30`,
          }}
        >
          <SIcon size={11} style={{ color: sevMeta.color }} />
        </div>

        {/* 标题 */}
        <div className="flex-1 min-w-0">
          <div className="text-[11px] text-gray-200 font-medium truncate">
            {issue.title}
          </div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="text-[8px] font-mono text-gray-600">
              {issue.category}
            </span>
            {projectName && (
              <span className="text-[8px] font-mono text-gray-600 truncate">
                · {projectName}
              </span>
            )}
          </div>
        </div>

        {/* 严重度 */}
        <span
          className="px-1 rounded text-[8px] font-mono uppercase flex-shrink-0"
          style={{
            background: `${sevMeta.color}20`,
            color: sevMeta.color,
          }}
        >
          {sevMeta.label}
        </span>

        {/* 状态切换 */}
        <select
          value={issue.status}
          onChange={(e) =>
            onStatusChange(e.target.value as DefectStatus)
          }
          className="px-1 py-0.5 rounded bg-white/[0.03] border border-white/[0.06] text-[8px] font-mono outline-none cursor-pointer flex-shrink-0"
          style={{ color: statMeta.color }}
        >
          <option value="open">待处理</option>
          <option value="fixing">修复中</option>
          <option value="fixed">已修复</option>
        </select>

        {/* 日期 */}
        <div className="flex flex-col items-end flex-shrink-0 w-[120px]">
          <span className="flex items-center gap-0.5 text-[8px] font-mono text-gray-500">
            <Calendar size={7} />
            发现 {formatDate(issue.foundDate)}
          </span>
          {issue.fixedDate && (
            <span className="text-[8px] font-mono text-emerald-500">
              修复 {formatDate(issue.fixedDate)}
            </span>
          )}
        </div>

        {/* 操作 */}
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <button
            onClick={onEdit}
            className="p-1 rounded hover:bg-white/[0.06] text-gray-500 hover:text-gray-300 transition-colors"
            title="编辑"
          >
            <Pencil size={9} />
          </button>
          <button
            onClick={onDelete}
            className="p-1 rounded hover:bg-white/[0.06] text-gray-500 hover:text-red-400 transition-colors"
            title="删除"
          >
            <Trash2 size={9} />
          </button>
        </div>
      </div>

      {/* 描述 */}
      {issue.description && (
        <p className="mt-1.5 text-[10px] text-gray-400 leading-snug">
          {issue.description}
        </p>
      )}
    </div>
  );
}

// ============================================
// 检查点行
// ============================================
function CheckpointRow({
  checkpoint,
  projectName,
  onEdit,
  onDelete,
  onStatusChange,
}: {
  checkpoint: QualityCheckpoint;
  projectName?: string;
  onEdit: () => void;
  onDelete: () => void;
  onStatusChange: (status: QualityCheckpoint["status"]) => void;
}) {
  const meta = CHECKPOINT_STATUS_META[checkpoint.status];
  return (
    <div className="rounded-md border border-white/[0.04] bg-white/[0.02] p-2.5">
      <div className="flex items-center gap-2">
        <ShieldCheck size={11} className="text-teal-400 flex-shrink-0" />

        {/* 名称 + 标准 */}
        <div className="flex-1 min-w-0">
          <div className="text-[11px] text-gray-200 font-medium truncate">
            {checkpoint.name}
          </div>
          <div className="text-[9px] font-mono text-gray-600 truncate">
            标准：{checkpoint.standard}
          </div>
        </div>

        {/* 项目 */}
        {projectName && (
          <span className="text-[9px] font-mono text-gray-500 truncate flex-shrink-0 max-w-[100px]">
            {projectName}
          </span>
        )}

        {/* 状态切换 */}
        <select
          value={checkpoint.status}
          onChange={(e) =>
            onStatusChange(e.target.value as QualityCheckpoint["status"])
          }
          className="px-1 py-0.5 rounded bg-white/[0.03] border border-white/[0.06] text-[8px] font-mono outline-none cursor-pointer flex-shrink-0"
          style={{ color: meta.color }}
        >
          <option value="pending">待检查</option>
          <option value="pass">通过</option>
          <option value="fail">不通过</option>
        </select>

        {/* 操作 */}
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <button
            onClick={onEdit}
            className="p-1 rounded hover:bg-white/[0.06] text-gray-500 hover:text-gray-300 transition-colors"
            title="编辑"
          >
            <Pencil size={9} />
          </button>
          <button
            onClick={onDelete}
            className="p-1 rounded hover:bg-white/[0.06] text-gray-500 hover:text-red-400 transition-colors"
            title="删除"
          >
            <Trash2 size={9} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// 缺陷编辑弹窗
// ============================================
function IssueEditModal({
  issue,
  projectOptions,
  onSubmit,
  onClose,
}: {
  issue: QualityIssue | null;
  projectOptions: { id: string; name: string }[];
  onSubmit: (data: Omit<QualityIssue, "id" | "createdAt" | "updatedAt">) => void;
  onClose: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    title: issue?.title || "",
    description: issue?.description || "",
    severity: issue?.severity || ("major" as DefectSeverity),
    status: issue?.status || ("open" as DefectStatus),
    category: issue?.category || "功能",
    projectId: issue?.projectId || "",
    foundDate: issue?.foundDate || today,
    fixedDate: issue?.fixedDate || "",
  });
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = () => {
    if (!form.title.trim()) {
      setError("请输入缺陷标题");
      return;
    }
    if (!form.foundDate) {
      setError("请选择发现日期");
      return;
    }
    onSubmit({
      title: form.title,
      description: form.description,
      severity: form.severity,
      status: form.status,
      category: form.category,
      projectId: form.projectId || undefined,
      foundDate: form.foundDate,
      fixedDate: form.status === "fixed" ? form.fixedDate || today : undefined,
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md max-h-[85vh] flex flex-col rounded-xl border border-teal-500/30 bg-[#0F1623] overflow-hidden shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-teal-500/15 border border-teal-500/30 flex items-center justify-center">
              {issue ? (
                <Pencil size={13} className="text-teal-400" />
              ) : (
                <Plus size={13} className="text-teal-400" />
              )}
            </div>
            <h3 className="text-[13px] font-semibold text-white">
              {issue ? "编辑缺陷" : "新增缺陷"}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-white/[0.06] text-gray-400 hover:text-white transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* 表单 */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {error && (
            <div className="px-3 py-2 rounded border border-red-500/30 bg-red-500/10 text-[10px] text-red-300 font-mono">
              {error}
            </div>
          )}

          <FormField label="缺陷标题">
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="简明描述缺陷"
              className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 placeholder-gray-600 outline-none focus:border-teal-500/30"
            />
          </FormField>

          <FormField label="缺陷描述">
            <textarea
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              placeholder="详细描述缺陷现象、复现步骤"
              rows={2}
              className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 placeholder-gray-600 outline-none focus:border-teal-500/30 resize-none"
            />
          </FormField>

          <div className="grid grid-cols-3 gap-3">
            <FormField label="严重程度">
              <select
                value={form.severity}
                onChange={(e) =>
                  setForm({ ...form, severity: e.target.value as DefectSeverity })
                }
                className="w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 outline-none cursor-pointer"
              >
                <option value="critical">致命</option>
                <option value="major">严重</option>
                <option value="minor">轻微</option>
              </select>
            </FormField>
            <FormField label="状态">
              <select
                value={form.status}
                onChange={(e) =>
                  setForm({ ...form, status: e.target.value as DefectStatus })
                }
                className="w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 outline-none cursor-pointer"
              >
                <option value="open">待处理</option>
                <option value="fixing">修复中</option>
                <option value="fixed">已修复</option>
              </select>
            </FormField>
            <FormField label="类别">
              <input
                type="text"
                value={form.category}
                onChange={(e) =>
                  setForm({ ...form, category: e.target.value })
                }
                placeholder="功能/性能/安全/UI"
                className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 placeholder-gray-600 outline-none focus:border-teal-500/30"
              />
            </FormField>
          </div>

          <FormField label="关联项目">
            <select
              value={form.projectId}
              onChange={(e) =>
                setForm({ ...form, projectId: e.target.value })
              }
              className="w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 outline-none cursor-pointer"
            >
              <option value="">不关联项目</option>
              {projectOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </FormField>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="发现日期">
              <input
                type="date"
                value={form.foundDate}
                onChange={(e) =>
                  setForm({ ...form, foundDate: e.target.value })
                }
                className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 outline-none focus:border-teal-500/30"
              />
            </FormField>
            <FormField label="修复日期（可选）">
              <input
                type="date"
                value={form.fixedDate}
                onChange={(e) =>
                  setForm({ ...form, fixedDate: e.target.value })
                }
                className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 outline-none focus:border-teal-500/30"
              />
            </FormField>
          </div>
        </div>

        {/* 底部按钮 */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-white/[0.06] bg-white/[0.01]">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-md border border-white/[0.06] text-[10px] font-mono text-gray-400 hover:bg-white/[0.04] transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-teal-500/20 border border-teal-500/30 text-[10px] font-mono text-teal-300 hover:bg-teal-500/30 transition-colors"
          >
            <Download size={10} />
            {issue ? "保存" : "创建"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// 检查点编辑弹窗
// ============================================
function CheckpointEditModal({
  checkpoint,
  projectOptions,
  onSubmit,
  onClose,
}: {
  checkpoint: QualityCheckpoint | null;
  projectOptions: { id: string; name: string }[];
  onSubmit: (
    data: Omit<QualityCheckpoint, "id" | "createdAt" | "updatedAt">
  ) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState({
    name: checkpoint?.name || "",
    standard: checkpoint?.standard || "",
    status: checkpoint?.status || ("pending" as QualityCheckpoint["status"]),
    projectId: checkpoint?.projectId || "",
  });
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = () => {
    if (!form.name.trim()) {
      setError("请输入检查点名称");
      return;
    }
    if (!form.standard.trim()) {
      setError("请输入质量标准");
      return;
    }
    onSubmit({
      name: form.name,
      standard: form.standard,
      status: form.status,
      projectId: form.projectId || undefined,
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md flex flex-col rounded-xl border border-teal-500/30 bg-[#0F1623] overflow-hidden shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-teal-500/15 border border-teal-500/30 flex items-center justify-center">
              {checkpoint ? (
                <Pencil size={13} className="text-teal-400" />
              ) : (
                <Plus size={13} className="text-teal-400" />
              )}
            </div>
            <h3 className="text-[13px] font-semibold text-white">
              {checkpoint ? "编辑检查点" : "新增检查点"}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-white/[0.06] text-gray-400 hover:text-white transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* 表单 */}
        <div className="flex-1 p-5 space-y-3">
          {error && (
            <div className="px-3 py-2 rounded border border-red-500/30 bg-red-500/10 text-[10px] text-red-300 font-mono">
              {error}
            </div>
          )}

          <FormField label="检查点名称">
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="如：单元测试覆盖率"
              className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 placeholder-gray-600 outline-none focus:border-teal-500/30"
            />
          </FormField>

          <FormField label="质量标准">
            <textarea
              value={form.standard}
              onChange={(e) => setForm({ ...form, standard: e.target.value })}
              placeholder="如：核心模块单元测试覆盖率 ≥ 80%"
              rows={2}
              className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 placeholder-gray-600 outline-none focus:border-teal-500/30 resize-none"
            />
          </FormField>

          <FormField label="关联项目">
            <select
              value={form.projectId}
              onChange={(e) =>
                setForm({ ...form, projectId: e.target.value })
              }
              className="w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 outline-none cursor-pointer"
            >
              <option value="">不关联项目（通用）</option>
              {projectOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="状态">
            <select
              value={form.status}
              onChange={(e) =>
                setForm({
                  ...form,
                  status: e.target.value as QualityCheckpoint["status"],
                })
              }
              className="w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 outline-none cursor-pointer"
            >
              <option value="pending">待检查</option>
              <option value="pass">通过</option>
              <option value="fail">不通过</option>
            </select>
          </FormField>
        </div>

        {/* 底部按钮 */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-white/[0.06] bg-white/[0.01]">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-md border border-white/[0.06] text-[10px] font-mono text-gray-400 hover:bg-white/[0.04] transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-teal-500/20 border border-teal-500/30 text-[10px] font-mono text-teal-300 hover:bg-teal-500/30 transition-colors"
          >
            <CheckCircle2 size={10} />
            {checkpoint ? "保存" : "创建"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// 表单字段包装
// ============================================
function FormField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label className="block text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}
